import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mountain, Lock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords don't match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Password updated — please log in.");
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-alpine-mist p-4">
                <div
                    data-testid="reset-missing-token"
                    className="max-w-md w-full bg-snowcap border border-ridge-line rounded-lg p-8 text-center"
                >
                    <Lock className="w-10 h-10 text-slate-gray mx-auto mb-3" />
                    <h1 className="font-display font-black text-2xl text-midnight-pine mb-2">
                        Missing reset token
                    </h1>
                    <p className="text-sm text-granite mb-4">
                        This link is incomplete or invalid. Request a fresh one from the login page.
                    </p>
                    <Link
                        to="/login"
                        className="inline-block px-5 py-2 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark"
                    >
                        Back to log in
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-alpine-mist p-4">
            <form
                onSubmit={submit}
                data-testid="reset-password-form"
                className="max-w-md w-full bg-snowcap border border-ridge-line rounded-lg p-8 space-y-5"
            >
                <div className="flex items-center gap-2 text-ember-orange mb-2">
                    <Mountain className="w-6 h-6" />
                    <span className="font-display font-black tracking-tight text-xl text-midnight-pine">
                        RealWorld Outdoors
                    </span>
                </div>
                <h1 className="font-display font-black text-2xl text-midnight-pine">
                    Choose a new password
                </h1>
                <p className="text-sm text-granite">
                    This link expires 1 hour after you requested it.
                </p>

                <label className="block">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-gray">
                        New password
                    </span>
                    <input
                        data-testid="reset-password-input"
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-md bg-white border border-ridge-line focus:border-ember-orange focus:outline-none"
                    />
                </label>
                <label className="block">
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-gray">
                        Confirm new password
                    </span>
                    <input
                        data-testid="reset-confirm-input"
                        type="password"
                        required
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-md bg-white border border-ridge-line focus:border-ember-orange focus:outline-none"
                    />
                </label>

                <button
                    type="submit"
                    disabled={loading}
                    data-testid="reset-submit-btn"
                    className="w-full py-3 rounded-md bg-ember-orange text-white font-bold hover:bg-ember-dark active:scale-95 disabled:opacity-60 transition-all"
                >
                    {loading ? "Updating…" : "Reset password"}
                </button>

                <div className="text-center text-sm text-slate-gray">
                    Remembered it?{" "}
                    <Link to="/login" className="font-bold text-ember-orange hover:underline">
                        Log in
                    </Link>
                </div>
            </form>
        </div>
    );
}
